import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ToneBadge } from "@/features/oversight/oversight-ui";
import { usdCompact } from "../dashboard-selectors";
import type { ChainDistributionItem } from "../dashboard-selectors";
import type { Wallet } from "@/types";
import type { ChainHealth } from "@/features/oversight/oversight-data";

const CHAIN_FILL: Record<string, string> = {
  "Ethereum Mainnet": "var(--ads-accent)",
  "Bitcoin Mainnet": "var(--ads-viz-amber)",
  "Polygon Mainnet": "var(--ads-pending)",
  "Solana Mainnet": "var(--ads-viz-teal)",
  "Ethereum Sepolia": "var(--ads-viz-slate-soft)",
  Others: "var(--ads-viz-slate-faint)",
};

const CHAIN_LABEL: Record<string, string> = {
  "Ethereum Mainnet": "Ethereum",
  "Bitcoin Mainnet": "Bitcoin",
  "Polygon Mainnet": "Polygon",
  "Solana Mainnet": "Solana",
  "Ethereum Sepolia": "Sepolia",
  Others: "Others",
};

export function ChainDistribution({
  items,
  total,
  wallets,
  chainHealth = [],
}: {
  items: ChainDistributionItem[];
  total: number;
  wallets: Wallet[];
  chainHealth?: ChainHealth[];
}) {
  const radius = 40;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;
  const segments = items.reduce<{ chain: string; pct: number; offset: number; fill: string }[]>(
    (acc, item) => {
      const offset = acc.reduce((sum, s) => sum + s.pct, 0);
      acc.push({
        chain: item.chain,
        pct: item.pct,
        offset,
        fill: CHAIN_FILL[item.chain] ?? "var(--ads-viz-slate)",
      });
      return acc;
    },
    []
  );

  const walletCounts = new Map<string, number>();
  for (const w of wallets) {
    if (!w.balanceUsd || w.balanceUsd <= 0) continue;
    walletCounts.set(w.chain, (walletCounts.get(w.chain) ?? 0) + 1);
  }

  const aria = items
    .map((i) => `${CHAIN_LABEL[i.chain] ?? i.chain} ${(i.pct * 100).toFixed(1)}%`)
    .join(", ");

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Assets by Chain</h2>
        <Link to="/wallets" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          View all <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      <div className="flex flex-1 flex-col items-center justify-between gap-5">
        <div className="relative shrink-0" role="img" aria-label={`Value by chain: ${aria}`}>
          <svg viewBox="0 0 100 100" className="size-36 md:size-40">
            {segments.map(({ chain, pct, offset, fill }) => {
              const dash = pct * circumference;
              return (
                <circle
                  key={chain}
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="none"
                  stroke={fill}
                  strokeWidth={stroke}
                  strokeDasharray={`${dash.toFixed(2)} ${circumference.toFixed(2)}`}
                  strokeDashoffset={-offset * circumference}
                  transform="rotate(-90 50 50)"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-base font-bold text-ink">{usdCompact.format(total)}</span>
            <span className="text-[9px] uppercase tracking-wider text-muted">Total AUM</span>
          </div>
        </div>

        <div className="w-full space-y-2.5">
          {items.map((item) => {
            const label = CHAIN_LABEL[item.chain] ?? item.chain;
            const pct = (item.pct * 100).toFixed(1);
            const count = item.chain === "Others" ? 0 : (walletCounts.get(item.chain) ?? 0);
            return (
              <div key={item.chain} className="flex items-center justify-between gap-3 text-sm">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="inline-block size-2.5 rounded-full"
                    style={{ backgroundColor: CHAIN_FILL[item.chain] ?? "var(--ads-viz-slate)" }}
                  />
                  <div className="min-w-0">
                    <div className="truncate font-medium text-ink">{label}</div>
                    {count > 0 && (
                      <div className="text-[10px] text-muted">
                        {count} wallet{count === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right text-xs tabular-nums">
                  <div className="font-semibold text-ink">{pct}%</div>
                  <div className="text-muted">{usdCompact.format(item.value)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {chainHealth.length > 0 && (
          <div className="w-full rounded-xl border border-line bg-bg p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted">Chain health</div>
            <div className="space-y-1.5">
              {chainHealth.map((chain) => (
                <Link
                  key={chain.id}
                  to="/monitoring/chain"
                  className="flex items-center justify-between gap-2 rounded-lg px-2 py-2 transition-colors hover:bg-surface"
                >
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-ink">{CHAIN_LABEL[chain.name] ?? chain.name}</div>
                    <div className="text-[10px] text-muted">Block {chain.blockHeight}</div>
                    <div className="text-[10px] text-muted">Observed {chain.lastObservation.split(" ").slice(1).join(" ")}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <ToneBadge tone={chain.statusTone} dot={false}>
                      {chain.statusLabel}
                    </ToneBadge>
                    <div className="mt-0.5 text-[10px] text-muted">Finality {chain.finality}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2 flex w-full items-center justify-between rounded-xl bg-bg px-4 py-3 text-xs">
          <span className="text-muted">
            {items.filter((i) => i.chain !== "Others").length} chain{items.filter((i) => i.chain !== "Others").length === 1 ? "" : "s"} tracked
          </span>
          <span className="font-bold text-ink">{usdCompact.format(total)} total</span>
        </div>
      </div>
    </div>
  );
}
